import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/atoms/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/atoms/popover";
import { Input } from "@/components/atoms/input";

export function HistoryMultiSelect({
  label,
  options,
  value,
  onChange,
  loading,
}: {
  label: string;
  options: { id: number; name: string | null }[];
  value: string;
  onChange: (value: string) => void;
  loading: boolean;
}) {
  const id = useId();
  const [search, setSearch] = useState("");
  const selected = value ? value.split(",").map(Number) : [];
  const normalize = (text: string) =>
    text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const visible = options.filter((option) =>
    normalize(`${option.name || ""} ${option.id}`).includes(normalize(search)),
  );
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 mb-2"
      >
        {label}
      </label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={loading}
            className="w-full justify-between h-10 border-gray-200"
          >
            {loading
              ? "Cargando..."
              : selected.length
                ? `${selected.length} seleccionados`
                : "Todos"}
            <ChevronDown aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[1000] w-80 max-w-[calc(100vw-2rem)] border-gray-200 !bg-white !text-gray-900 p-3 shadow-xl"
        >
          <Input
            aria-label={`Buscar ${label.toLowerCase()}`}
            placeholder="Buscar por nombre o ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange("")}
            className="my-2"
          >
            Limpiar selección
          </Button>
          <fieldset className="max-h-60 overflow-auto space-y-1">
            <legend className="sr-only">{label}</legend>
            {visible.map((option) => (
              <label
                key={option.id}
                className="flex items-center gap-2 rounded px-2 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={selected.includes(option.id)}
                  onChange={(e) => {
                    onChange(
                      (e.target.checked
                        ? [...selected, option.id]
                        : selected.filter((item) => item !== option.id)
                      ).join(","),
                    );
                  }}
                />
                <span className="text-sm">
                  {option.name || `ID ${option.id}`}
                </span>
              </label>
            ))}
            {!visible.length && (
              <p className="text-sm text-gray-500 p-2">Sin coincidencias</p>
            )}
          </fieldset>
        </PopoverContent>
      </Popover>
      {loading && (
        <p className="text-xs text-gray-500 mt-1">
          Disponible cuando terminen de cargar las opciones.
        </p>
      )}
    </div>
  );
}
